import React from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { translations } from '@/locale';
import { VendorAdd } from '@/domains/vendor';

import { InstanceProps } from '@/components/Modal';
import Typography from '@/components/Typography';
import { inviteVendor } from '@/services/api/vendors';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

const fields = translations.inputs;

const formSchema = z.object({
  name: z.string().min(1, 'Vendor name is required'),
  email: z.string().email('Invalid email address').optional().or(z.literal(''))
});

type FormValues = z.infer<typeof formSchema>;

export interface Props extends InstanceProps {
  onSubmit: () => void;
}

const AddVendorModal: React.FC<Props & WrappedComponentProps> = ({ close, onSubmit, intl }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: ''
    }
  });

  async function handleSubmit(values: FormValues) {
    try {
      await inviteVendor(values as VendorAdd);
      onSubmit();
      close();
    } catch (error) {
      console.error('Failed to invite vendor:', error);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Typography is="span" className="font-bold">
          <FormattedMessage id={translations.modals.addVendor.title} />
        </Typography>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {intl.formatMessage({ id: fields.vendorName.label })}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder={intl.formatMessage({ id: fields.vendorName.placeholder })} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{intl.formatMessage({ id: fields.email.label })}</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={intl.formatMessage({ id: fields.email.placeholder })} {...field} />
                </FormControl>
                <FormMessage />
                <Typography is="p" size="sm" className="text-muted-foreground mt-1">
                  Email is not mandatory. If you enter a valid email address, the person will automatically get an
                  invitation to join as a Vendor Admin.
                </Typography>
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={!form.formState.isDirty || form.formState.isSubmitting}>
            <FormattedMessage id={translations.modals.addVendor.confirm} />
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default injectIntl(AddVendorModal);
